import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const InviteOpen = ({ onOpen, invite }) => {
  const [phase, setPhase] = useState(0);
  const isCouples = invite?.flow_type === 'couples';
  const senderName = invite?.sender_profile?.name || invite?.sender_display_name || 'Someone special';

  const handleOpen = () => {
    setPhase(1);
    setTimeout(() => setPhase(2), 800);
    setTimeout(() => setPhase(3), 1400);
    setTimeout(() => setPhase(4), 2200);
    setTimeout(() => onOpen(), 2600);
  };

  const shakeKeyframes = {
    rotate: [0, -3, 3, -3, 3, -2, 2, 0],
    y: [0, -4, 0, -4, 0, -2, 0],
  };

  return (
    <motion.div
      exit={{ opacity: 0, scale: 1.1, filter: 'blur(8px)' }}
      className="absolute inset-0 z-50 flex flex-col items-center justify-center p-6 text-center bg-gradient-to-b from-pink-50 via-white to-rose-50"
    >
      <div className="mb-4">
        <img src="https://cloud.sharemyimage.com/2026/08/16/DayClap-Logo.png" alt="DayClap" className="h-14 mx-auto" />
      </div>

      {/* Envelope + Paper container */}
      <div className="relative mx-auto mb-8 w-64 h-72 flex items-center justify-center">
        {/* Paper sliding out */}
        <AnimatePresence>
          {phase >= 2 && (
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.7 }}
              animate={
                phase >= 3
                  ? { y: -80, opacity: 1, scale: 1 }
                  : { y: -20, opacity: 0.8, scale: 0.85 }
              }
              transition={{ duration: phase >= 3 ? 0.8 : 0.5, ease: 'easeOut' }}
              className="absolute z-20 w-52"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 text-left relative overflow-hidden">
                {/* Paper lines */}
                <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="border-b border-blue-300" style={{ height: '24px' }} />
                  ))}
                </div>
                {/* Red margin line */}
                <div className="absolute left-8 top-0 bottom-0 w-px bg-red-200 opacity-30" />

                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                      <span className="text-sm">💌</span>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">From</p>
                      <p className="text-xs font-bold text-gray-800">{senderName}</p>
                    </div>
                  </div>

                  {invite?.message && (
                    <p className="text-xs text-gray-600 italic leading-relaxed line-clamp-3 mb-2">
                      "{invite.message}"
                    </p>
                  )}

                  {invite?.invitation_question && (
                    <p className="text-xs font-semibold text-gray-700 mt-2">
                      {invite.invitation_question}
                    </p>
                  )}

                  <div className="flex gap-1.5 mt-3">
                    <div className="flex-1 bg-green-50 text-green-600 text-center py-1 rounded text-[9px] font-bold">❤️</div>
                    <div className="flex-1 bg-amber-50 text-amber-600 text-center py-1 rounded text-[9px] font-bold">🤔</div>
                    <div className="flex-1 bg-gray-50 text-gray-500 text-center py-1 rounded text-[9px] font-bold">❌</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Envelope */}
        <motion.div
          animate={
            phase === 0 ? shakeKeyframes :
            phase >= 3 ? { y: 60, opacity: 0, scale: 0.8 } :
            { y: 20, scale: 0.95 }
          }
          transition={
            phase === 0
              ? { repeat: Infinity, duration: 2.5, ease: 'easeInOut', repeatDelay: 1 }
              : { duration: 0.6, ease: 'easeInOut' }
          }
          className="w-52 h-40 mx-auto relative z-10"
          style={{ perspective: 800 }}
        >
          {/* Envelope body */}
          <div className="absolute inset-0 rounded-2xl shadow-2xl border-2 bg-gradient-to-b from-amber-50 to-amber-100 border-amber-300">
            <div className="absolute inset-2 rounded-xl opacity-20" style={{
              backgroundImage: 'radial-gradient(circle, #ec4899 1px, transparent 1px)',
              backgroundSize: '12px 12px'
            }} />
          </div>

          {/* Envelope flap */}
          <motion.div
            animate={phase >= 1 ? { rotateX: 180, opacity: 0.5 } : { rotateX: 0 }}
            transition={{ duration: 0.7, ease: 'easeInOut' }}
            className="absolute top-0 left-0 right-0 h-[55%] rounded-t-2xl border-2 border-b-0 bg-gradient-to-b from-amber-200 to-amber-100 border-amber-300 origin-top"
            style={{ clipPath: 'polygon(0 0, 50% 85%, 100% 0)', backfaceVisibility: 'hidden' }}
          />

          {/* Wax seal */}
          <motion.div
            animate={phase >= 1 ? { scale: 0, opacity: 0 } : { scale: [1, 1.05, 1] }}
            transition={phase >= 1 ? { duration: 0.3 } : { repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
            className="absolute top-[42%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full flex items-center justify-center shadow-xl z-10 bg-gradient-to-br from-red-400 to-red-600"
          >
            <span className="text-white text-xl">💌</span>
          </motion.div>

          {/* From label */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <p className="text-xs font-semibold text-amber-800">From {senderName}</p>
          </div>

          {/* Stamp */}
          <div className="absolute top-3 right-3 w-8 h-10 bg-white rounded border border-gray-200 flex items-center justify-center shadow-sm">
            <span className="text-xs">❤️</span>
          </div>
        </motion.div>

        {/* Floating hearts */}
        {phase === 0 && [...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 1, 0],
              y: [-20, -60 - Math.random() * 40],
              x: [(Math.random() - 0.5) * 80],
              scale: [0.5, 1, 0.5]
            }}
            transition={{
              repeat: Infinity,
              duration: 3 + Math.random() * 2,
              delay: i * 0.5,
              ease: 'easeOut'
            }}
            className="absolute top-1/2 left-1/2 text-lg pointer-events-none"
          >
            {i % 2 === 0 ? '❤️' : '💌'}
          </motion.div>
        ))}

        {/* Burst particles on open */}
        {phase >= 1 && phase < 3 && (
          <>
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={`burst-${i}`}
                initial={{ opacity: 1, x: 0, y: 0, scale: 0 }}
                animate={{
                  opacity: 0,
                  x: (Math.random() - 0.5) * 300,
                  y: -Math.random() * 250 - 50,
                  scale: Math.random() + 0.5,
                  rotate: Math.random() * 360
                }}
                transition={{ duration: 1.2, delay: i * 0.03 }}
                className="absolute top-1/2 left-1/2 text-2xl pointer-events-none"
              >
                {['❤️', '💌', '✨', '💕', '🎉', '⭐'][i % 6]}
              </motion.div>
            ))}
          </>
        )}
      </div>

      <AnimatePresence mode="wait">
        {phase === 0 && (
          <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.h1
              animate={{ y: [0, -3, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="text-2xl font-bold text-gray-800 mb-2"
            >
              You Have Something Special!
            </motion.h1>
            <p className="text-gray-500 mb-2">
              {senderName} made something just for you.
            </p>
            <p className="text-xs text-gray-400 mb-8">No sign up needed to open 💌</p>

            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleOpen}
              className="font-bold py-4 px-10 rounded-full shadow-xl text-lg bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-pink-300 hover:scale-105 transition-transform"
            >
              Open Envelope 💌
            </motion.button>
          </motion.div>
        )}

        {phase >= 1 && phase < 4 && (
          <motion.div key="opening" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <p className="text-lg font-semibold text-gray-700 animate-pulse">
              ✨ Opening...
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default InviteOpen;